import { useLayoutEffect } from "react";

/** Tab-local pixel hints only. Restoration is a single best-effort attempt:
 * clamp to available content, then the user owns scrolling (including drags).
 *
 * P-10: writing `scrollTop` inside the layout effect forced a synchronous
 * layout of the freshly mounted dashboard during React's commit (about 250 ms
 * on a throttled phone), before passive effects changed the DOM again. The
 * write now happens in the next animation frame, where the browser lays the
 * page out anyway, and only when the position actually differs. */
export function useDashboardScroll(element: HTMLElement | null, key: string): void {
  useLayoutEffect(() => {
    if (!element) return;
    const storageKey = "tinkr.location:scroll:" + key;
    let target = 0;
    try { target = Number(sessionStorage.getItem(storageKey) ?? 0); } catch { /* optional */ }
    if (!Number.isFinite(target) || target < 0 || target > 1_000_000) target = 0;
    let restored = false;
    const restore = () => {
      restored = true;
      if (element.scrollTop !== target) element.scrollTop = target;
    };
    const frame = typeof window.requestAnimationFrame === "function" ? window.requestAnimationFrame(restore) : undefined;
    if (frame === undefined) restore();
    const save = () => {
      // Until the restore ran, the element still shows the previous key's position.
      if (!restored) return;
      try { sessionStorage.setItem(storageKey, String(Math.round(element.scrollTop))); } catch { /* optional */ }
    };
    // No observer can re-pin a scrollbar drag or later autoscroll. Keep the
    // clamped position even if a deferred panel never reaches the saved height.
    element.addEventListener("scroll", save, { passive: true });
    window.addEventListener("pagehide", save);
    return () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      save();
      element.removeEventListener("scroll", save);
      window.removeEventListener("pagehide", save);
    };
  }, [element, key]);
}
